import React from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import DEFAULT_CONTENT from '../content/PAGE_CONTENT.json';
import LegalFooter from '../components/LegalFooter';

/**
 * Thank-you page for both live offers. Reads ?offer= (safe fallback: four-week)
 * and the selected date from router state only, so a direct open or refresh
 * never shows a wrong date. Nothing here depends on the API.
 */
const COPY = {
  'four-week': {
    heading: 'You\u2019re One Step Closer to Your Performance Reset.',
    sub: 'Your Founding Cohort request has been received.',
    body: [
      'Thank you for your interest in The Performance Reset \u2014 a four-week guided breathwork experience for established financial advisors.',
      'We\u2019ve received your request. Your next step is to complete the secure participant intake and consent form linked in your confirmation email.',
    ],
    steps: [
      ['Check your inbox.', 'Look for the email containing your secure intake and consent form.'],
      ['Complete your intake.', 'Your information will be reviewed before participation can be approved.'],
      ['Receive your payment invitation.', 'Approved participants will receive a private link to complete the $399 founding-cohort payment.'],
      ['Prepare for your first session.', 'After payment and registration confirmation, you\u2019ll receive the Zoom details and preparation instructions.'],
    ],
    note: 'Your first live session begins Monday, January 11, 2027, at 8:00 AM Eastern.',
    smallPrint: 'The founding cohort is limited to 10 participants. A request does not reserve or guarantee a place.',
  },
  'single-journey': {
    heading: 'Your Reset Starts Here.',
    sub: 'Your Single Journey request has been received.',
    body: [
      'Thank you for taking the first step toward your Performance Reset experience.',
      'We\u2019ve received your request for the Single Live Group Journey. Check your inbox for an email with a link to your secure participant intake and consent form.',
    ],
    steps: [
      ['Check your inbox.', 'Your confirmation email includes the secure participant intake and consent form.'],
      ['Complete your intake.', 'The information is reviewed to determine whether the live experience is appropriate for you.'],
      ['Receive your payment invitation.', 'After your intake has been reviewed and participation approved, you\u2019ll receive a private SamCart link to complete the $59 payment.'],
      ['Get ready for your journey.', 'Once payment and registration are confirmed, you\u2019ll receive the Zoom details and preparation instructions for your selected session.'],
    ],
    note: '',
    smallPrint: 'Submitting a request does not guarantee acceptance or reserve a place. Your registration is confirmed only after screening approval, payment, and confirmation.',
  },
};

export default function ThankYou() {
  const [params] = useSearchParams();
  const { state } = useLocation();
  const offer = params.get('offer') === 'single-journey' ? 'single-journey' : 'four-week';
  const copy = COPY[offer];
  const preferredDate = offer === 'single-journey' && typeof state?.preferredDate === 'string' ? state.preferredDate : '';
  const emailFailed = state?.confirmed === false;
  const c = DEFAULT_CONTENT;

  return (
    <div className="page ty" data-testid="thank-you-page" data-offer={offer}>
      <section className="band center" style={{ background: 'var(--bg)' }}>
        <div className="wrap narrow">
          <img src={c.meta.logo} alt="The Performance Reset" className="logo ty-logo" />
          <h1 className="display" data-testid="thank-you-heading">{copy.heading}</h1>
          <p className="hero-kicker" data-testid="thank-you-sub">{copy.sub}</p>
          <div className="body" data-testid="thank-you-intro">
            {copy.body.map((p) => <p key={p}>{p}</p>)}
            {preferredDate && <p data-testid="thank-you-date">Your selected session: <b>{preferredDate}</b></p>}
            {emailFailed && (
              <p className="req-error" data-testid="thank-you-email-warning">
                Your request was saved, but we couldn’t send the confirmation email just now. Karla will email you the secure intake form personally.
              </p>
            )}
          </div>

          <h2 className="display ty-h2">What Happens Next</h2>
          <ol className="steps ty-steps" data-testid="thank-you-steps">
            {copy.steps.map(([title, desc], i) => (
              <li key={title}>
                <span className="step-n">{i + 1}</span>
                <div><p className="mt">{title}</p><p className="md">{desc}</p></div>
              </li>
            ))}
          </ol>
          {copy.note && <p className="lead ty-note" data-testid="thank-you-note">{copy.note}</p>}
          <p className="cite" data-testid="thank-you-smallprint">{copy.smallPrint}</p>
          <p className="cite">Don’t see our email? Check your spam or promotions folder. Questions? Contact <a href="mailto:karla@inner-hq.com">karla@inner-hq.com</a>.</p>
          <div className="cta-wrap c">
            <Link className="cta" to="/" data-testid="thank-you-home">Return to The Performance Reset <span aria-hidden="true">→</span></Link>
          </div>
        </div>
      </section>
      <LegalFooter footer={c.footer} />
    </div>
  );
}
